import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import "./Profile.css";
import { API_URL } from "../config";
import TruckstopHelpModal from "../components/TruckstopHelpModal";
import CHRobinsonHelpModal from "../components/CHRobinsonHelpModal";
import { useTheme } from "../contexts/ThemeContext";
import { Link } from "react-router-dom";
import ProfileDetails from "../components/ProfileDetails";
import { INTERNAL_EMAILS } from "../utils/internalEmails";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState({
    truckstop_integration_id: "",
    ch_t_number: "",
    company_name: "",
    phone_number: "",
    mc_number: "",
    dot_number: "",
    truck_type: "V", // Default to Van
    origin_city: "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isCHRobinsonHelpModalOpen, setIsCHRobinsonHelpModalOpen] =
    useState(false);

  useEffect(() => {
    loadProfileData();
  }, []);

  const themeCtx = useTheme();
  const [themeChoice, setThemeChoice] = useState(
    () => localStorage.getItem("theme") || "system",
  );

  const applyTheme = (mode) => {
    localStorage.setItem("theme", mode);
    const root = document.documentElement;
    const body = document.body;
    const systemIsDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    const isDark = mode === "dark" || (mode === "system" && systemIsDark);
    root.dataset.theme = mode === "system" ? "" : mode; // e.g., [data-theme="dark"]
    body.dataset.theme = root.dataset.theme;
    root.classList.toggle("dark", isDark); // e.g., .dark {...}
    body.classList.toggle("dark", isDark);
  };

  useEffect(() => {
    applyTheme(themeChoice);
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => themeChoice === "system" && applyTheme("system");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (themeCtx?.theme && themeCtx.theme !== themeChoice) {
      setThemeChoice(themeCtx.theme);
      applyTheme(themeCtx.theme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeCtx?.theme]);

  const onThemeChange = (e) => {
    const next = e.target.value;
    setThemeChoice(next);
    themeCtx?.setTheme?.(next);
    applyTheme(next);
  };

  const loadProfileData = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API_URL}/user/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.data.dot_authorized) {
        setError("Unable to verify Dot Number.");
        setTimeout(() => setError(""), 3000);
        return;
      }
      setProfile(response.data);
    } catch (err) {
      console.error("Error loading profile:", err);
      setError("Failed to load profile data");
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveProfile = async () => {
    console.log(profile);
    setIsLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      // Format the T-number before sending to backend
      let formattedProfile = { ...profile };
      if (formattedProfile.ch_t_number) {
        let tNumber = formattedProfile.ch_t_number.trim();
        // Remove any existing "T" prefix and "-" characters
        tNumber = tNumber.replace(/^T-?/, "").replace(/-/g, "");
        // Add "T" prefix if not present
        if (tNumber && !tNumber.startsWith("T")) {
          tNumber = "T" + tNumber;
        }
        formattedProfile.ch_t_number = tNumber;
      }
      const response = await axios.post(
        `${API_URL}/dot_authorization/check-dot`,
        {
          dot_number: profile.dot_number,
        },
      );

      if (
        !(
          response.data.dot_authorized ||
          (user?.email && INTERNAL_EMAILS.includes(user.email))
        )
      ) {
        setError("Unable to verify Dot Number.");
        setTimeout(() => setError(""), 3000);
        return;
      }
      formattedProfile.dot_authorized = true;
      await axios.post(
        `${API_URL}/user/profile`,
        {
          ...formattedProfile,
          email: user.email,
          name: user.name,
          subscription_type: 2,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      await refreshUser();
      setSuccessMessage("Profile saved successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error saving profile:", err);
      setError("Failed to save profile");
      setTimeout(() => setError(""), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API_URL}/stripe/create-customer-portal-session`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.url) {
        // Open Stripe customer portal in new tab
        window.open(response.data.url, "_blank");
      } else {
        setError("Failed to open subscription management. Please try again.");
        setTimeout(() => setError(""), 3000);
      }
    } catch (err) {
      console.error("Error opening subscription management:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to open subscription management. Please try again.",
      );
      setTimeout(() => setError(""), 3000);
    }
  };

  return (
    <div className="profile-container">
      {/* User Header */}
      <div className="profile-header">
        {user?.picture && (
          <img src={user.picture} alt={user.name} className="profile-picture" />
        )}
        <div className="profile-info">
          <h1>{user?.name || "User Profile"}</h1>
          <p>{user?.email}</p>
        </div>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "flex-end",
            gap: 12,
          }}
        >
          <label
            htmlFor="theme-select"
            style={{
              display: "block",
              fontSize: 12,
              color: "var(--text-muted)",
            }}
          >
            Theme
          </label>
          <select
            id="theme-select"
            value={themeChoice}
            onChange={onThemeChange}
            className="form-input"
            style={{ minWidth: 140 }}
          >
            <option value="system">System</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
          {/* Manage Plan */}
          {user?.subscription_type >= 1 ? (
            <button
              onClick={handleManageSubscription}
              className={`saveButton`}
              style={{
                background:
                  "linear-gradient(45deg, var(--primary), var(--primary-dark, var(--primary)))",
                color: "var(--button-text)",
                border: "none",
                borderRadius: "12px",
                padding: "10px 16px",
                fontSize: 14,
                fontWeight: 600,
                whiteSpace: "nowrap",
                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                cursor: "pointer",
              }}
            >
              Manage Subscription
            </button>
          ) : (
            <Link
              to="/subscribe"
              className={`saveButton`}
              style={{
                textDecoration: "none",
                background:
                  "linear-gradient(45deg, var(--primary), var(--primary-dark, var(--primary)))",
                color: "var(--button-text)",
                border: "none",
                borderRadius: "12px",
                padding: "10px 16px",
                fontSize: 14,
                fontWeight: 600,
                whiteSpace: "nowrap",
                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              }}
            >
              Upgrade Plan
            </Link>
          )}
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="success-message">
          <i className="fas fa-check-circle"></i>
          {successMessage}
        </div>
      )}
      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}

      {/* Profile Information Section */}
      <section className="profile-section">
        <div className="section-header">
          <h2>Company Information</h2>
          <p>Update your company details and integration settings</p>
        </div>
        <div className="form-container">
          <ProfileDetails
            profile={profile}
            handleProfileChange={handleProfileChange}
            accountType={profile.account_type}
            setIsHelpModalOpen={setIsHelpModalOpen}
            setIsCHRobinsonHelpModalOpen={setIsCHRobinsonHelpModalOpen}
          />
          <button
            onClick={handleSaveProfile}
            className={`save-button ${isLoading ? "loading" : ""}`}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save Company Information"}
          </button>
        </div>
      </section>

      <TruckstopHelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
      <CHRobinsonHelpModal
        isOpen={isCHRobinsonHelpModalOpen}
        onClose={() => setIsCHRobinsonHelpModalOpen(false)}
      />
    </div>
  );
}
